import React from 'react';
import styled from 'styled-components';
import { AiOutlineFullscreen, AiOutlineFullscreenExit } from 'react-icons/ai';
import { useComputed } from '@preact/signals-react';
import { useDebouncedCallback } from 'use-debounce';
import { MonacoEditor } from '@/features/editor/widgets/code-editors/monaco-editor';
import { useAiComponentContext } from '@/features/editor/widgets/ai-component/context';
import { CompoundChange } from '@/pkg/sdk';

export function AiComponentCode() {
  const selectedTab = useAiComponentContext((ctx) => ctx.selectedCodeTab);
  const newChange = useAiComponentContext((ctx) => ctx.newChange);
  const setHtml = useAiComponentContext((ctx) => ctx.setHtml);
  const setCss = useAiComponentContext((ctx) => ctx.setCss);
  const setJs = useAiComponentContext((ctx) => ctx.setJs);
  const setEditManifest = useAiComponentContext((ctx) => ctx.setEditManifest);
  const isFullscreen = useAiComponentContext((ctx) => ctx.isFullScreen);
  const toggleFullscreen = useAiComponentContext((ctx) => ctx.toggleFullScreen);

  const html = useComputed(
    () => (newChange?.value?.block?.value as CompoundChange)?.html || ``,
  );
  const css = useComputed(
    () => (newChange?.value?.block?.value as CompoundChange)?.css || ``,
  );
  const js = useComputed(
    () => (newChange?.value?.block?.value as CompoundChange)?.js || ``,
  );

  const currentCode = useComputed(() => {
    if (selectedTab.value === `HTML`) {
      return html.value;
    }
    if (selectedTab.value === `CSS`) {
      return css.value;
    }
    if (selectedTab.value === `JS`) {
      return js.value;
    }
    if (selectedTab.value === `M`) {
      return JSON.stringify(
        (newChange?.value?.block?.value as CompoundChange)?.editManifest,
        null,
        2,
      );
    }
    return ``;
  });

  const currentLang = useComputed(() => {
    if (selectedTab.value === `HTML`) {
      return `html`;
    }
    if (selectedTab.value === `CSS`) {
      return `css`;
    }
    if (selectedTab.value === `JS`) {
      return `javascript`;
    }
    if (selectedTab.value === `M`) {
      return `json`;
    }
    return `html`;
  });

  const currentOnCodeChange = useComputed(() => {
    if (selectedTab.value === `HTML`) {
      return setHtml;
    }
    if (selectedTab.value === `CSS`) {
      return setCss;
    }
    if (selectedTab.value === `JS`) {
      return setJs;
    }
    if (selectedTab.value === `M`) {
      return setEditManifest;
    }
    return () => {
      console.warn(`No onCodeChange function defined for this tab.`);
    };
  });

  const onCodeChanged = useDebouncedCallback(currentOnCodeChange.value, 1000);

  return (
    <Wrapper>
      <FullscreenButton onClick={toggleFullscreen}>
        {isFullscreen.value ? (
          <AiOutlineFullscreenExit size={20} />
        ) : (
          <AiOutlineFullscreen size={20} />
        )}
      </FullscreenButton>
      <MonacoEditor
        code={currentCode.value}
        onCodeChange={onCodeChanged}
        lang={currentLang.value}
        readOnly={false}
        autoFormat
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const FullscreenButton = styled.button`
  appearance: none;
  outline: none;
  border: none;
  background-color: white;
  box-shadow: 0 8px 10px 0 rgba(105, 122, 231, 0.11);
  height: 3.5rem;
  width: 3.5rem;
  border-radius: 100%;
  color: #969696;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;

  position: absolute;
  right: 1.5rem;
  top: 1rem;
  z-index: 1;

  &:hover {
    color: black;
  }

  &:active {
    color: #969696;
  }

  transition: color 0.2s ease-in-out;
`;
